import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiService } from '../../services/api';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../context/AuthContext';

const PortfolioManagementPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();

  const { user: loggedInUser, businesses } = useAuth();
  const loggedInUserTypeId = String(loggedInUser?.user_type?.id || '');
  const isStaff = loggedInUserTypeId === '3';

  // User types state
  const [userTypes, setUserTypes] = useState([]);
  const [userTypesLoading, setUserTypesLoading] = useState(false);
  const [selectedUserTypeId, setSelectedUserTypeId] = useState('');

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  // Searchable user input state
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userSearchRef = useRef(null);
  const userDropdownRef = useRef(null);

  // Selected user
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // Businesses state
  const [bizList, setBizList] = useState([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [businessesError, setBusinessesError] = useState('');

  // Merchant assignments: { [businessCode]: { [merchantId]: boolean } }
  const [assignments, setAssignments] = useState({});
  // Editing state per business card
  const [editingBusiness, setEditingBusiness] = useState({});

  // Business search
  const [businessSearch, setBusinessSearch] = useState('');

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // Fetch user types and users on mount
  useEffect(() => {
    fetchUserTypes();
    fetchUsers();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        userDropdownRef?.current && !userDropdownRef?.current?.contains(e?.target) &&
        userSearchRef?.current && !userSearchRef?.current?.contains(e?.target)
      ) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch businesses when user is selected
  useEffect(() => {
    if (selectedUserId) {
      const user = users?.find(u => u?.user_id === selectedUserId);
      setSelectedUser(user || null);
      setAssignments({});
      setEditingBusiness({});
      fetchBusinessesAndPortfolio(selectedUserId);
    } else {
      setSelectedUser(null);
      setBizList([]);
      setAssignments({});
      setEditingBusiness({});
    }
  }, [selectedUserId]);

  const fetchUserTypes = async () => {
    setUserTypesLoading(true);
    try {
      const response = await apiService?.get('/portal/users/types');
      const data = response?.data;
      const allTypes = data?.details || [];

      if (isStaff) {
        // Staff sees all user types
        setUserTypes(allTypes);
      } else {
        // Partner or Merchant only sees their own user type
        const filtered = allTypes?.filter(type => String(type?.id) === loggedInUserTypeId);
        setUserTypes(filtered);
      }
    } catch (err) {
      // silently fail — user types are optional filter
    } finally {
      setUserTypesLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const userType = loggedInUser?.user_type?.id;
      const response = await apiService?.getUsers(businesses, userType);
      const data = response?.data;
      const list = data?.details || [];
      setUsers(list);
    } catch (err) {
      setUsersError(err?.response?.data?.status_description || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchBusinessesAndPortfolio = async (userId) => {
    setBusinessesLoading(true);
    setBusinessesError('');
    try {
      const params = new URLSearchParams();
      businesses?.forEach(code => params?.append('codes', code));
      const [bizResponse, portfolioResponse] = await Promise.all([
        apiService?.get(`/portal/business?${params?.toString()}`),
        apiService?.get(`/portal/portfolio/${userId}/user`),
      ]);

      const bizRawList = bizResponse?.data?.details || [];
      const portfolioDetails = portfolioResponse?.data?.details || [];

      // Handle API response where each business already has a merchants array
      // Also handle flat records (legacy) where merchant_id is a top-level field
      const bizMap = {};
      bizRawList?.forEach(record => {
        const code = record?.business_code;
        if (!bizMap?.[code]) {
          bizMap[code] = {
            business_code: code,
            business_name: record?.business_name,
            business_nature: record?.business_nature,
            merchants: [],
          };
        }
        // If the record already has a merchants array, use it
        if (Array.isArray(record?.merchants)) {
          record?.merchants?.forEach(m => {
            const alreadyAdded = bizMap?.[code]?.merchants?.some(existing => existing?.merchant_id === m?.merchant_id);
            if (!alreadyAdded && m?.merchant_id) {
              bizMap?.[code]?.merchants?.push({
                merchant_id: m?.merchant_id,
                status: m?.status,
              });
            }
          });
        } else if (record?.merchant_id) {
          // Legacy flat format: merchant_id is a top-level field
          const alreadyAdded = bizMap?.[code]?.merchants?.some(m => m?.merchant_id === record?.merchant_id);
          if (!alreadyAdded) {
            bizMap?.[code]?.merchants?.push({
              merchant_id: record?.merchant_id,
              status: record?.status,
            });
          }
        }
      });
      const bizList = Object.values(bizMap);

      setBizList(bizList);
      setBusinessSearch('');

      // Build a set of assigned business_code + merchant_id pairs
      // Use String() and trim() for robust comparison regardless of type differences
      const init = {};
      bizList?.forEach(biz => {
        init[biz?.business_code] = {};
        biz?.merchants?.forEach(m => {
          const isAssigned = portfolioDetails?.some(
            p =>
              String(p?.business_code)?.trim() === String(biz?.business_code)?.trim() &&
              String(p?.merchant_id)?.trim() === String(m?.merchant_id)?.trim()
          );
          init[biz?.business_code][m?.merchant_id] = isAssigned;
        });
      });
      setAssignments(init);
    } catch (err) {
      setBusinessesError(err?.response?.data?.status_description || 'Failed to load businesses');
    } finally {
      setBusinessesLoading(false);
    }
  };

  const toggleEdit = (businessCode) => {
    setEditingBusiness(prev => ({
      ...prev,
      [businessCode]: !prev?.[businessCode],
    }));
  };

  const toggleMerchant = (businessCode, merchantId) => {
    setAssignments(prev => ({
      ...prev,
      [businessCode]: {
        ...prev?.[businessCode],
        [merchantId]: !prev?.[businessCode]?.[merchantId],
      },
    }));
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      // Build businesses array — only include businesses with at least one checked merchant
      const businessesPayload = Object.entries(assignments)
        ?.map(([businessCode, merchants]) => ({
          business_code: businessCode,
          merchant_ids: Object.entries(merchants)
            ?.filter(([, checked]) => checked)
            ?.map(([mid]) => mid),
        }))
        ?.filter(entry => entry?.merchant_ids?.length > 0);

      const payload = {
        user_id: selectedUserId,
        operation: 'GRANT',
        businesses: businessesPayload,
        parent_user_type: parseInt(loggedInUser?.user_type?.id, 10),
      };

      await apiService?.post('/portal/portfolio', payload);
      setSaveSuccess('Portfolio assignments saved successfully!');
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch (err) {
      setSaveError(err?.response?.data?.status_description || 'Failed to save assignments');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedUserId('');
    setSelectedUser(null);
    setUserSearch('');
    setSelectedUserTypeId('');
    setBizList([]);
    setAssignments({});
    setEditingBusiness({});
    setSaveError('');
    setSaveSuccess('');
  };

  const getAssignedCount = (businessCode) => {
    const merchants = assignments?.[businessCode] || {};
    return Object.values(merchants)?.filter(Boolean)?.length;
  };

  // Returns true if a business has at least one assigned merchant
  const isBusinessAssigned = (businessCode) => {
    return getAssignedCount(businessCode) > 0;
  };

  // Filter businesses by search, then sort assigned first, then exclude businesses with no merchants
  const filteredBusinesses = businessSearch?.trim()
    ? bizList?.filter(biz =>
        biz?.business_name?.toLowerCase()?.includes(businessSearch?.trim()?.toLowerCase())
      )
    : bizList;

  const sortedFilteredBusinesses = [...(filteredBusinesses || [])]
    ?.filter(biz => biz?.merchants?.length > 0)
    ?.sort((a, b) => {
      const aAssigned = isBusinessAssigned(a?.business_code) ? 0 : 1;
      const bAssigned = isBusinessAssigned(b?.business_code) ? 0 : 1;
      return aAssigned - bAssigned;
    });

  // Select All helpers
  const getAllMerchantsChecked = () => {
    if (sortedFilteredBusinesses?.length === 0) return false;
    return sortedFilteredBusinesses?.every(biz =>
      biz?.merchants?.every(m => assignments?.[biz?.business_code]?.[m?.merchant_id])
    );
  };

  const getAllMerchantsIndeterminate = () => {
    const allChecked = getAllMerchantsChecked();
    if (allChecked) return false;
    return sortedFilteredBusinesses?.some(biz =>
      biz?.merchants?.some(m => assignments?.[biz?.business_code]?.[m?.merchant_id])
    );
  };

  const handleSelectAll = () => {
    const allChecked = getAllMerchantsChecked();
    setAssignments(prev => {
      const next = { ...prev };
      sortedFilteredBusinesses?.forEach(biz => {
        next[biz?.business_code] = { ...prev?.[biz?.business_code] };
        biz?.merchants?.forEach(m => {
          next[biz?.business_code][m?.merchant_id] = !allChecked;
        });
      });
      return next;
    });
  };

  // Filter users by selected user type
  const typeFilteredUsers = selectedUserTypeId
    ? users?.filter(u => String(u?.user_type?.id) === String(selectedUserTypeId))
    : users;

  // Further filter by search text (first_name or last_name starting with typed letters)
  const searchFilteredUsers = userSearch?.trim()
    ? typeFilteredUsers?.filter(u => {
        const q = userSearch?.trim()?.toLowerCase();
        return (
          u?.first_name?.toLowerCase()?.startsWith(q) ||
          u?.last_name?.toLowerCase()?.startsWith(q)
        );
      })
    : typeFilteredUsers;

  const handleUserTypeChange = (e) => {
    setSelectedUserTypeId(e?.target?.value);
    // Reset user selection when type changes
    setSelectedUserId('');
    setSelectedUser(null);
    setUserSearch('');
    setBizList([]);
    setAssignments({});
    setEditingBusiness({});
  };

  const handleUserSearchInput = (e) => {
    setUserSearch(e?.target?.value);
    setShowUserDropdown(true);
    // Clear selected user if search changes
    if (selectedUser) {
      setSelectedUserId('');
      setSelectedUser(null);
      setBizList([]);
      setAssignments({});
      setEditingBusiness({});
    }
  };

  const handleSelectUserFromDropdown = (user) => {
    setSelectedUserId(user?.user_id);
    setUserSearch(`${user?.first_name} ${user?.last_name}`);
    setShowUserDropdown(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-text-primary">Portfolio Management</h1>
            <p className="text-text-secondary mt-1">Assign users to businesses and merchants.</p>
          </div>

          {/* Success / Error banners */}
          {saveSuccess && (
            <div className="mb-4 flex items-center gap-2 bg-success/10 border border-success/30 text-success rounded-lg px-4 py-3 text-sm">
              <Icon name="CheckCircle" size={16} />
              {saveSuccess}
            </div>
          )}
          {saveError && (
            <div className="mb-4 flex items-center gap-2 bg-error/10 border border-error/30 text-error rounded-lg px-4 py-3 text-sm">
              <Icon name="AlertCircle" size={16} />
              {saveError}
            </div>
          )}

          {/* Two-panel layout */}
          <div className="flex gap-6 items-start">
            {/* Left Panel — Select User */}
            <div className="w-72 flex-shrink-0 bg-surface border border-border rounded-xl p-5 shadow-sm">
              <h2 className="text-base font-semibold text-text-primary mb-4">Select User</h2>

              {usersError && (
                <p className="text-xs text-error mb-3">{usersError}</p>
              )}

              {/* Step 1: User Type Dropdown */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-text-secondary mb-1.5">
                  User Type
                </label>
                <div className="relative">
                  <select
                    value={selectedUserTypeId}
                    onChange={handleUserTypeChange}
                    disabled={userTypesLoading || usersLoading}
                    className="w-full appearance-none bg-background border border-border rounded-lg px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60 cursor-pointer"
                  >
                    <option value="">
                      {userTypesLoading ? 'Loading types...' : 'Select User Type'}
                    </option>
                    {userTypes?.map(ut => (
                      <option key={ut?.id} value={ut?.id}>
                        {ut?.user_type}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    {userTypesLoading
                      ? <Icon name="Loader2" size={14} className="text-text-secondary animate-spin" />
                      : <Icon name="ChevronDown" size={14} className="text-text-secondary" />
                    }
                  </div>
                </div>
              </div>

              {/* Step 2: Searchable User Input */}
              <div className="mb-1">
                <label className="block text-xs font-medium text-text-secondary mb-1.5">
                  Search User
                </label>
                <div className="relative">
                  <div className="relative">
                    <input
                      ref={userSearchRef}
                      type="text"
                      value={userSearch}
                      onChange={handleUserSearchInput}
                      onFocus={() => selectedUserTypeId && setShowUserDropdown(true)}
                      placeholder={
                        !selectedUserTypeId
                          ? 'Select a user type first'
                          : usersLoading
                          ? 'Loading users...' :'Type to search users...'
                      }
                      disabled={!selectedUserTypeId || usersLoading}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60 placeholder:text-text-secondary/60"
                    />
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                      {usersLoading
                        ? <Icon name="Loader2" size={14} className="text-text-secondary animate-spin" />
                        : <Icon name="Search" size={14} className="text-text-secondary" />
                      }
                    </div>
                  </div>

                  {/* Dropdown list */}
                  {showUserDropdown && selectedUserTypeId && searchFilteredUsers?.length > 0 && (
                    <div
                      ref={userDropdownRef}
                      className="absolute z-20 top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-52 overflow-y-auto"
                    >
                      {searchFilteredUsers?.map(u => (
                        <button
                          key={u?.user_id}
                          type="button"
                          onMouseDown={() => handleSelectUserFromDropdown(u)}
                          className={`w-full text-left px-3 py-2.5 text-sm hover:bg-muted transition-colors flex items-center gap-2 ${
                            selectedUserId === u?.user_id ? 'bg-primary/5 text-primary' : 'text-text-primary'
                          }`}
                        >
                          <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name="User" size={11} className="text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{u?.first_name} {u?.last_name}</p>
                            <p className="text-xs text-text-secondary truncate">{u?.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* No results message */}
                  {showUserDropdown && selectedUserTypeId && userSearch?.trim() && searchFilteredUsers?.length === 0 && (
                    <div
                      ref={userDropdownRef}
                      className="absolute z-20 top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-lg"
                    >
                      <p className="px-3 py-3 text-sm text-text-secondary text-center">No users found</p>
                    </div>
                  )}
                </div>
              </div>

              {selectedUser && (
                <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={14} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {selectedUser?.first_name} {selectedUser?.last_name}
                      </p>
                      <p className="text-xs text-text-secondary truncate">{selectedUser?.email}</p>
                    </div>
                  </div>
                  {selectedUser?.user_type?.user_type && (
                    <span className="mt-2 inline-block text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                      {selectedUser?.user_type?.user_type}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Right Panel — User Portfolio */}
            <div className="flex-1 bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              <div className="p-5 border-b border-border">
                <h2 className="text-base font-semibold text-text-primary">
                  {selectedUser
                    ? `${selectedUser?.first_name} ${selectedUser?.last_name}'s Portfolio` :'User Portfolio'}
                </h2>
                {selectedUser ? (
                  <p className="text-sm text-text-secondary mt-0.5">
                    Manage business and merchant assignments
                  </p>
                ) : (
                  <p className="text-sm text-text-secondary mt-0.5">Select a user to manage their portfolio</p>
                )}
              </div>

              <div className="p-5">
                {/* Empty state — no user selected */}
                {!selectedUserId && (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mb-3">
                      <Icon name="Users" size={24} className="text-text-secondary" />
                    </div>
                    <p className="text-sm font-medium text-text-primary">No user selected</p>
                    <p className="text-xs text-text-secondary mt-1">Choose a user type, then search and select a user from the left panel</p>
                  </div>
                )}

                {/* Loading businesses */}
                {selectedUserId && businessesLoading && (
                  <div className="flex items-center justify-center py-16">
                    <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
                    <span className="text-sm text-text-secondary">Loading businesses...</span>
                  </div>
                )}

                {/* Businesses error */}
                {selectedUserId && !businessesLoading && businessesError && (
                  <div className="flex items-center gap-2 text-error text-sm py-4">
                    <Icon name="AlertCircle" size={16} />
                    {businessesError}
                  </div>
                )}

                {/* Business cards */}
                {selectedUserId && !businessesLoading && !businessesError && (
                  <div className="space-y-4">
                    {bizList?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Icon name="Building2" size={32} className="text-text-secondary mb-2" />
                        <p className="text-sm text-text-secondary">No businesses found</p>
                      </div>
                    ) : (
                      <>
                        {/* Business search + Select All toolbar */}
                        <div className="flex items-center gap-3 mb-2">
                          {/* Search input */}
                          <div className="relative flex-1">
                            <input
                              type="text"
                              value={businessSearch}
                              onChange={e => setBusinessSearch(e?.target?.value)}
                              placeholder="Search by business name..."
                              className="w-full bg-background border border-border rounded-lg px-3 py-2 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-text-secondary/60"
                            />
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                              <Icon name="Search" size={14} className="text-text-secondary" />
                            </div>
                          </div>
                          {/* Select All toggle */}
                          <label className="flex items-center gap-2 cursor-pointer select-none flex-shrink-0 px-3 py-2 bg-background border border-border rounded-lg hover:border-primary/50 transition-colors">
                            <div className="relative flex-shrink-0" onClick={handleSelectAll}>
                              <div
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                                  getAllMerchantsChecked()
                                    ? 'bg-primary border-primary': getAllMerchantsIndeterminate()? 'bg-primary/30 border-primary': 'bg-surface border-border hover:border-primary'
                                }`}
                              >
                                {getAllMerchantsChecked() && <Icon name="Check" size={10} className="text-white" />}
                                {getAllMerchantsIndeterminate() && <div className="w-2 h-0.5 bg-primary rounded" />}
                              </div>
                            </div>
                            <span className="text-xs font-medium text-text-primary whitespace-nowrap" onClick={handleSelectAll}>
                              Select All
                            </span>
                          </label>
                        </div>

                        {/* No search results */}
                        {sortedFilteredBusinesses?.length === 0 && (
                          <div className="flex flex-col items-center justify-center py-10 text-center">
                            <Icon name="SearchX" size={28} className="text-text-secondary mb-2" />
                            <p className="text-sm text-text-secondary">No businesses match your search</p>
                          </div>
                        )}

                        {sortedFilteredBusinesses?.map(biz => {
                          const isEditing = editingBusiness?.[biz?.business_code];
                          const assignedCount = getAssignedCount(biz?.business_code);
                          const assigned = isBusinessAssigned(biz?.business_code);

                          const assignedMerchants = biz?.merchants?.filter(m =>
                            assignments?.[biz?.business_code]?.[m?.merchant_id]
                          );
                          const unassignedMerchants = biz?.merchants?.filter(m =>
                            !assignments?.[biz?.business_code]?.[m?.merchant_id]
                          );

                          return (
                            <div key={biz?.business_code} className={`border rounded-xl overflow-hidden ${assigned ? 'border-primary/40' : 'border-border'}`}>
                              {/* Business header */}
                              <div className={`flex items-center gap-3 px-4 py-3 border-b ${assigned ? 'bg-primary/5 border-primary/20' : 'bg-muted/40 border-border'}`}>
                                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Icon name="Building2" size={16} className="text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-text-primary truncate">{biz?.business_name}</p>
                                  <p className="text-xs text-text-secondary">{biz?.business_code} · {biz?.business_nature}</p>
                                </div>
                                {assigned && (
                                  <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full font-medium mr-1">Assigned</span>
                                )}
                                <span className="text-xs text-text-secondary mr-2">
                                  {assignedCount}/{biz?.merchants?.length} assigned
                                </span>
                              </div>

                              {/* Merchants section */}
                              <div className="px-4 py-3">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Merchants</span>
                                  <button
                                    onClick={() => toggleEdit(biz?.business_code)}
                                    className={`text-xs px-3 py-1 rounded border font-medium transition-colors ${
                                      isEditing
                                        ? 'bg-primary text-white border-primary' : 'bg-surface text-primary border-border hover:border-primary'
                                    }`}
                                  >
                                    {isEditing ? 'Done' : 'Edit'}
                                  </button>
                                </div>

                                {/* Assigned merchants */}
                                {assignedMerchants?.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-xs font-medium text-success mb-1.5 flex items-center gap-1">
                                      <Icon name="CheckCircle" size={11} className="text-success" />
                                      Assigned
                                    </p>
                                    <div className="space-y-2">
                                      {assignedMerchants?.map(merchant => (
                                        <label
                                          key={merchant?.merchant_id}
                                          className="flex items-center gap-3 cursor-pointer group"
                                        >
                                          <div className="relative flex-shrink-0">
                                            <input
                                              type="checkbox"
                                              checked
                                              onChange={() => toggleMerchant(biz?.business_code, merchant?.merchant_id)}
                                              className="sr-only"
                                            />
                                            <div className="w-4 h-4 rounded border-2 flex items-center justify-center transition-colors bg-primary border-primary group-hover:border-primary/70">
                                              <Icon name="Check" size={10} className="text-white" />
                                            </div>
                                          </div>
                                          <span className="text-sm text-text-primary font-medium">
                                            {merchant?.merchant_id}
                                          </span>
                                          {merchant?.status === '1' && (
                                            <span className="ml-auto text-xs text-success bg-success/10 px-1.5 py-0.5 rounded-full">Active</span>
                                          )}
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Unassigned merchants */}
                                {unassignedMerchants?.length > 0 && (
                                  <div>
                                    {assignedMerchants?.length > 0 && (
                                      <div className="border-t border-border my-2" />
                                    )}
                                    <p className="text-xs font-medium text-text-secondary mb-1.5 flex items-center gap-1">
                                      <Icon name="Circle" size={11} className="text-text-secondary" />
                                      Unassigned
                                    </p>
                                    <div className="space-y-2">
                                      {unassignedMerchants?.map(merchant => (
                                        <label
                                          key={merchant?.merchant_id}
                                          className="flex items-center gap-3 cursor-pointer group"
                                        >
                                          <div className="relative flex-shrink-0">
                                            <input
                                              type="checkbox"
                                             
                                              onChange={() => toggleMerchant(biz?.business_code, merchant?.merchant_id)}
                                              className="sr-only"
                                            />
                                            <div className="w-4 h-4 rounded border-2 flex items-center justify-center transition-colors bg-surface border-border group-hover:border-primary">
                                            </div>
                                          </div>
                                          <span className="text-sm text-text-secondary">
                                            {merchant?.merchant_id}
                                          </span>
                                          {merchant?.status === '1' && (
                                            <span className="ml-auto text-xs text-success bg-success/10 px-1.5 py-0.5 rounded-full">Active</span>
                                          )}
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Footer actions */}
              {selectedUserId && !businessesLoading && (
                <div className="px-5 py-4 border-t border-border flex items-center justify-between">
                  <button
                    onClick={() => navigate('/users')}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    View All Users
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-text-primary border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saveLoading}
                      className="px-5 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center gap-2"
                    >
                      {saveLoading && <Icon name="Loader2" size={14} className="animate-spin" />}
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PortfolioManagementPage;
